import { UseFormReturn, useWatch } from "react-hook-form";

import orderCore, { DeliveryStatusArr, OrderStatusArr, PaymentStatusArr } from "../../core/order.core";
import { useTranslation } from "../../core/translation.core";
import { OrderListFilter } from "../../types/order";
import DateRangePicker from "../form/dateRangePicker.comp";
import { FancyCheckbox, FancyTextInput } from "../reuseables/form.comp";
import XMarkIcon from "../../icons/xMark.icon";
import ArrowRightIcon from "../../icons/arrowRight.icon";
import AdjustmentsHorizontalIcon from "../../icons/adjustmentsHorizontal.icon";
import Modal from "../reuseables/modal.comp";
import { Dispatch, RefObject, SetStateAction, useRef, useState } from "react";
import customerCore from "../../core/customer.core";
import { useQuery } from "@tanstack/react-query";
import productCore, { getProduct } from "../../core/product.core";
import { ProductSearchRst2 } from "../../types/product";
import localeCore from "../../core/locale.core";
import { useEffect } from "react";
import { ShipTypeOption } from "../../types/shipSetting";
import { CustomerList } from "../../types/customer";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";

export default function OrderListFilterComp({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [modalOpened, setModalOpened] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <section data-type="section">
      <FilterBar btnRef={btnRef} setModalOpened={setModalOpened} methods={methods} />
      <FilterModal btnRef={btnRef} modalOpened={modalOpened} setModalOpened={setModalOpened} methods={methods} />
    </section>
  );
}

function FilterModal({
  btnRef,
  modalOpened,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLButtonElement>;
  modalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<OrderListFilter, any>;
}) {
  const { t, msg } = useTranslation();

  if (!modalOpened) return null;

  return (
    <Modal
      btnRef={btnRef}
      setModalOpened={setModalOpened}
      cssHeight="top-[20dvh] md:top-1/2 h-[60dvh] md:h-[900px] max-h-[90dvh] overflow-y-auto "
    >
      <div className="flex gap-8 flex-wrap py-14 px-8">
        <h1 className="basis-full text-center text-xl">{t(msg.order.orderListFilter)}</h1>
        <FancyTextInput register={() => methods.register("orderNumber")} placeholder={t(msg.order.orderNumber)} />
        <div className="block border p-2">
          {t(msg.order.orderStatus)}
          {OrderStatusArr.map((o) => (
            <FancyCheckbox key={o} register={methods.register("orderStatuses")} value={o}>
              {orderCore.getOrderStatusLocale(o, { t, msg })}
            </FancyCheckbox>
          ))}
        </div>

        <div className="block border p-2">
          {t(msg.order.paymentStatus)}
          {PaymentStatusArr.map((p) => (
            <FancyCheckbox key={p} register={methods.register("paymentStatuses")} value={p}>
              {orderCore.getPaymentStatusLocale(p, { t, msg })}
            </FancyCheckbox>
          ))}
        </div>

        <div className="block border p-2">
          {t(msg.delivery.deliveryStatus)}
          {DeliveryStatusArr.map((d) => (
            <FancyCheckbox key={d} register={methods.register("deliveryStatuses")} value={d}>
              {orderCore.getDeliveryStatusLocale(d, { t, msg })}
            </FancyCheckbox>
          ))}
        </div>

        <div className="block border p-2">
          <DateRangePicker methods={methods} />
        </div>

        <div className="block border p-2 w-full">
          <div className="mb-2">{t(msg.customer.customer)}</div>
          <CustomerFilter methods={methods} />
        </div>

        <div className="block border p-2 w-full">
          <div className="mb-2">{t(msg.product.product)}</div>
          <ProductFilter methods={methods} />
        </div>

        <div className="block border p-2 w-full">
          <div className="mb-2">{t(msg.ship.shipType)}</div>
          <DeliveryMethodFilter methods={methods} />
        </div>
      </div>
    </Modal>
  );
}

function FilterBar({
  btnRef,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLButtonElement>;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<OrderListFilter, any>;
}) {
  return (
    <button
      id="order-list-filter-bar-btn"
      ref={btnRef}
      className="border rounded-lg py-2 px-4 items-center flex justify-between w-full"
      onClick={() => setModalOpened(true)}
    >
      <div className="flex flex-wrap gap-2">
        <DateOptionItem methods={methods} />
        <DateRange methods={methods} />
        <OrderStatuses methods={methods} />
        <PaymentStatuses methods={methods} />
        <DeliveryStatuses methods={methods} />
        <OrderNumber methods={methods} />
        <Customer methods={methods} />
        <Product methods={methods} />
        <DeliveryMethod methods={methods} />
      </div>
      <AdjustmentsHorizontalIcon />
    </button>
  );
}

function DateOptionItem({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const dateOption = useWatch({ control: methods.control, name: "dateOption" });

  function getText() {
    switch (dateOption) {
      case "last7Days":
        return t(msg.date.last7Days);
      case "today":
        return t(msg.date.today);
      case "last30Days":
        return t(msg.date.last30Days);
      case "last365Days":
        return t(msg.date.last365Days);
      default:
        return "";
    }
  }

  if (!dateOption) return null;

  return <Badge bgColor="bg-admin-blue">{getText()}</Badge>;
}

function DateRange({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [date, dateOption] = useWatch({ control: methods.control, name: ["date", "dateOption"] });

  if (dateOption) return null;

  return (
    <Badge bgColor="bg-admin-blue">
      {date.from} <ArrowRightIcon /> {date.to}
    </Badge>
  );
}

function OrderStatuses({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const orderStatuses = useWatch({ control: methods.control, name: "orderStatuses" });

  if (!orderStatuses) return null;
  if (orderStatuses.length === 0) return null;

  return (
    <>
      {orderStatuses.map((orderStatus) => (
        <Badge
          bgColor="bg-admin-green"
          key={orderStatus}
          onClose={() => {
            methods.setValue(
              "orderStatuses",
              orderStatuses.filter((v) => v !== orderStatus)
            );
          }}
        >
          {orderCore.getOrderStatusLocale(orderStatus, { t, msg })}
        </Badge>
      ))}
    </>
  );
}

function PaymentStatuses({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const paymentStatuses = useWatch({ control: methods.control, name: "paymentStatuses" });

  if (!paymentStatuses) return null;
  if (paymentStatuses.length === 0) return null;

  return (
    <>
      {paymentStatuses.map((paymentStatus) => (
        <Badge
          bgColor="bg-admin-blue"
          key={paymentStatus}
          onClose={() => {
            methods.setValue(
              "paymentStatuses",
              paymentStatuses.filter((v) => v !== paymentStatus)
            );
          }}
        >
          {orderCore.getPaymentStatusLocale(paymentStatus, { t, msg })}
        </Badge>
      ))}
    </>
  );
}

function DeliveryStatuses({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const deliveryStatuses = useWatch({ control: methods.control, name: "deliveryStatuses" });

  if (!deliveryStatuses) return null;
  if (deliveryStatuses.length === 0) return null;

  return (
    <>
      {deliveryStatuses.map((deliveryStatus) => (
        <Badge
          bgColor="bg-admin-green"
          key={deliveryStatus}
          onClose={() => {
            methods.setValue(
              "deliveryStatuses",
              deliveryStatuses.filter((v) => v !== deliveryStatus)
            );
          }}
        >
          {orderCore.getDeliveryStatusLocale(deliveryStatus, { t, msg })}
        </Badge>
      ))}
    </>
  );
}

function OrderNumber({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const orderNumber = useWatch({ control: methods.control, name: "orderNumber" });

  if (!orderNumber) return null;

  return (
    <Badge bgColor="bg-admin-blue" onClose={() => methods.setValue("orderNumber", "")}>
      {orderNumber}
    </Badge>
  );
}

function CustomerFilter({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [isOpened, setIsOpened] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  const customerId = useWatch({ control: methods.control, name: "customerId" });

  const { data: customers = { list: [], size: 0 } as CustomerList, isLoading: isLoadingCustomers } = useQuery({
    queryKey: [searchText],
    queryFn: (context) => customerCore.loadCustomers({ search: context.queryKey[0] as string }),
    enabled: searchText !== "",
  });

  const { data: selectedCustomerData } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: (context) => customerCore.getSingle(context.queryKey[1] as string),
    enabled: !!customerId && !customers.list.find((c) => c.id === customerId),
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref || !ref.current) return;
      const isOpened = (ref.current as any).getAttribute("data-state");

      if (!isOpened) return;

      const isWithin = (e.target as any).closest("#order-customer-filter");

      if (!isWithin) {
        setIsOpened(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const selectedCustomer = customers.list.find((c) => c.id === customerId) || selectedCustomerData;
  const customerName = selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "";

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base " +
    "flex items-center px-3 cursor-pointer";

  if (selectedCustomer) {
    return (
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <p className="font-medium uppercase">
            {selectedCustomer.firstName} {selectedCustomer.lastName}
          </p>
          <p className="text-sm">{selectedCustomer.email}</p>
        </div>
        <button
          onClick={() => {
            methods.setValue("customerId", "");
            setIsOpened(false);
          }}
          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
        >
          <XMarkIcon />
        </button>
      </div>
    );
  }

  return (
    <div id="order-customer-filter" ref={ref} data-state={isOpened ? "true" : "false"}>
      <div className={css} onClick={() => setIsOpened(!isOpened)}>
        <span className="flex-1 text-left">{customerName || t(msg.product.searchCustomer)}</span>
        <span className="text-xs text-gray-500">▼</span>
      </div>
      {isOpened && (
        <div className="w-full border border-t-0 rounded-b-md bg-white dark:bg-her-backblue shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2">
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md dark:bg-her-blackOlive dark:text-white"
              placeholder={t(msg.product.searchCustomer)}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>{" "}
          {isLoadingCustomers && (
            <div className="p-4">
              <LoadingOvalCenterIcon title="please wait, retrieving customers" />
            </div>
          )}
          {searchText && !isLoadingCustomers && (
            <ul id="order-customer-filter-list" className="py-2">
              {customers.list.map((c) => {
                return (
                  <li
                    key={c.id}
                    onClick={() => {
                      methods.setValue("customerId", c.id);
                      setIsOpened(false);
                      setSearchText("");
                    }}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">
                      {c.firstName} {c.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{c.email}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Customer({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const customerId = useWatch({ control: methods.control, name: "customerId" });

  const { data: customer } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: (context) => customerCore.getSingle(context.queryKey[1] as string),
    enabled: !!customerId,
  });

  if (!customerId || !customer) return null;

  const customerName = `${customer.firstName} ${customer.lastName}`;

  return (
    <Badge bgColor="bg-admin-blue" onClose={() => methods.setValue("customerId", "")} dataValue={customer.email}>
      {customerName} ({customer.email})
    </Badge>
  );
}

function ProductFilter({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [isOpened, setIsOpened] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  const productId = useWatch({ control: methods.control, name: "productId" });

  const { data: products = { list: [], size: 0 } as ProductSearchRst2 } = useQuery({
    queryKey: [searchText],
    queryFn: (context) => productCore.searchProduct(context.queryKey[0] as string),
    enabled: searchText !== "",
  });

  const { data: selectedProductData } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      if (!productId) return null;
      try {
        return await getProduct(productId);
      } catch {
        return null;
      }
    },
    enabled: !!productId && !products.list.find((p) => p.id === productId),
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref || !ref.current) return;
      const isOpened = (ref.current as any).getAttribute("data-state");

      if (!isOpened) return;

      const isWithin = (e.target as any).closest("#order-product-filter");

      if (!isWithin) {
        setIsOpened(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const selectedProduct = products.list.find((p) => p.id === productId) || selectedProductData;
  const productName = selectedProduct ? localeCore.getLocaleValueDefaultFallback(selectedProduct.name) : "";

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base " +
    "flex items-center px-3 cursor-pointer";

  return (
    <div id="order-product-filter" ref={ref} data-state={isOpened ? "true" : "false"}>
      <div className={css} onClick={() => setIsOpened(!isOpened)}>
        <span className="flex-1 text-left">{productName || t(msg.product.selectProduct)}</span>
        <span className="text-xs text-gray-500">▼</span>
      </div>
      {isOpened && (
        <div className="w-full border border-t-0 rounded-b-md bg-white dark:bg-her-backblue shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2">
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md dark:bg-her-blackOlive dark:text-white"
              placeholder={t(msg.product.searchProduct)}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {searchText && (
            <ul className="py-2">
              {products.list.map((p) => {
                const name = localeCore.getLocaleValueDefaultFallback(p.name);
                return (
                  <li
                    key={p.id}
                    onClick={() => {
                      methods.setValue("productId", p.id);
                      setIsOpened(false);
                      setSearchText("");
                    }}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">{name}</div>
                    <div className="text-sm text-gray-500">
                      {t(msg.product.price)}: ${p.price} | {t(msg.product.sku)}: {p.sku}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Product({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const productId = useWatch({ control: methods.control, name: "productId" });

  const { data: product } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      if (!productId) return null;
      try {
        return await getProduct(productId);
      } catch {
        return null;
      }
    },
    enabled: !!productId,
  });

  if (!productId || !product) return null;

  const productName = localeCore.getLocaleValueDefaultFallback(product.name);

  return (
    <Badge bgColor="bg-admin-blue" onClose={() => methods.setValue("productId", "")}>
      {productName}
    </Badge>
  );
}

function DeliveryMethodFilter({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const shipType = useWatch({ control: methods.control, name: "shipType" });

  const shipTypeOptions: ShipTypeOption[] = ["basic", "sf", "pickup"];

  return (
    <select
      {...methods.register("shipType")}
      className="w-full h-9 border rounded-md px-2 dark:bg-her-backblue"
      value={shipType || ""}
      onChange={(e) => {
        const value = e.target.value as ShipTypeOption | "";
        if (value) {
          methods.setValue("shipType", value);
        } else {
          methods.setValue("shipType", undefined);
        }
      }}
    >
      <option value="">{t(msg.form.select)}</option>
      {shipTypeOptions.map((type) => (
        <option key={type} value={type}>
          {t(msg.ship[`shipType${type.charAt(0).toUpperCase() + type.slice(1)}`])}
        </option>
      ))}
    </select>
  );
}

function DeliveryMethod({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { t, msg } = useTranslation();
  const shipType = useWatch({ control: methods.control, name: "shipType" });

  if (!shipType) return null;

  const shipTypeLabel = t(msg.ship[`shipType${shipType.charAt(0).toUpperCase() + shipType.slice(1)}`]);

  return (
    <Badge bgColor="bg-admin-blue" onClose={() => methods.setValue("shipType", undefined)}>
      {shipTypeLabel}
    </Badge>
  );
}

// admin-green
// admin-blue
function Badge({
  bgColor,
  children,
  onClose,
  dataValue,
}: {
  bgColor: string;
  children: React.ReactNode;
  onClose?: () => void;
  dataValue?: string | number;
}) {
  return (
    <div className={`${bgColor} text-white px-4 py-2 rounded-full inline-flex gap-2`} data-value={dataValue}>
      {children}
      {onClose && (
        <button
          className="fill-white"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
